import type { MediaObject } from '@/domain/shared/media';
import { FileRequestType, type GetImageUrlsResponse, type UploadImageResponse } from '@/types';
import { getApiEndpoints } from '@/services/api/endpoints';
import { createAppAsyncThunk } from '@/store/typedAsyncThunk';
import type {
  BatchDeleteMediaResult,
  DeleteMediaArgs,
  DeleteMediaBatchArgs,
  FetchSignedMediaUrlsArgs,
  UploadMediaArgs,
} from './mediaTypes';

function isRecordString(value: unknown): value is Record<string, string> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function isResolvableFileUrl(value: string): boolean {
  return Boolean(value) && (value.includes('://') || value.startsWith('data:') || value.startsWith('blob:'));
}

function calculateExpiryMs(ttlSeconds: number): number {
  return Date.now() + ttlSeconds * 1000;
}

function normalizeKeys(keys: string[]): string[] {
  return Array.from(new Set(keys.map((key) => key?.trim()).filter((key): key is string => Boolean(key))));
}

function shouldFetchMedia(existing?: MediaObject, force = false): boolean {
  if (force) return true;
  if (!existing) return true;
  if (!existing.expiresAt) return true;
  return existing.expiresAt <= Date.now();
}

function toMediaObject(args: {
  key: string;
  rawValue: string;
  folder: FetchSignedMediaUrlsArgs['folder'];
  expiresAt: number;
}): MediaObject {
  const isCorrupt = !isResolvableFileUrl(args.rawValue);

  return {
    key: args.key,
    url: args.rawValue,
    expiresAt: args.expiresAt,
    error: isCorrupt ? args.rawValue : undefined,
    loading: false,
    savedToCloud: true,
    mediaNotFoundOrCorrupt: isCorrupt,
    savedToFolder: args.folder,
  };
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      if (typeof reader.result !== 'string') {
        reject(new Error('Failed to read file'));
        return;
      }

      const [, base64 = ''] = reader.result.split(',', 2);
      resolve(base64);
    };

    reader.onerror = () => reject(reader.error ?? new Error('Failed to read file'));
    reader.readAsDataURL(blob);
  });
}

export const fetchSignedMediaUrls = createAppAsyncThunk<
  { folder: FetchSignedMediaUrlsArgs['folder']; items: MediaObject[] },
  FetchSignedMediaUrlsArgs
>('media/fetchSignedMediaUrls', async ({ propertyID, folder, filesKeys, force = false }, thunkApi) => {
  const {
    extra: { config },
    getState,
    rejectWithValue,
  } = thunkApi;
  const { apiService } = config;

  try {
    const normalizedKeys = normalizeKeys(filesKeys);
    const catalog = getState().media.catalog.byKey;
    const ttlSeconds = getState().media.config.ttlSeconds;
    const keysToFetch = normalizedKeys.filter((key) => shouldFetchMedia(catalog[key], force));

    if (keysToFetch.length === 0) {
      return {
        folder,
        items: normalizedKeys.map((key) => catalog[key]).filter((item): item is MediaObject => Boolean(item)),
      };
    }

    const endpoint = getApiEndpoints().core.storage.fetchSignedMediaUrls;
    const response = await apiService.post<GetImageUrlsResponse>(endpoint, {
      requestType: FileRequestType.GetImageUrls,
      propertyID,
      folder,
      filesKeys: keysToFetch,
    });

    if (!isRecordString(response)) {
      throw new Error('Invalid storage response');
    }

    const expiresAt = calculateExpiryMs(ttlSeconds);
    const items = keysToFetch.map((key) =>
      toMediaObject({
        key,
        rawValue: response[key] ?? 'NotFound',
        folder,
        expiresAt,
      }),
    );

    return { folder, items };
  } catch (err) {
    const error = err as Error;
    return rejectWithValue({ message: error.message || 'Failed to fetch signed media URLs' });
  }
});

export const uploadMedia = createAppAsyncThunk<MediaObject, UploadMediaArgs>(
  'media/uploadMedia',
  async (
    { propertyID, folder, key, file, contentType, metadata },
    { extra: { config }, getState, rejectWithValue },
  ) => {
    const { apiService } = config;

    try {
      const endpoint = getApiEndpoints().core.storage.uploadMedia;
      const ttlSeconds = getState().media.config.ttlSeconds;
      const fileContent = await blobToBase64(file);
      const response = await apiService.post<UploadImageResponse>(endpoint, {
        requestType: FileRequestType.UploadImage,
        propertyID,
        folder,
        fileName: key,
        fileContent,
        contentType: contentType || file.type || 'application/octet-stream',
      });

      if (response.message !== 'UploadSuccess' || !response.data?.signedUrl) {
        throw new Error('Upload failed');
      }

      return {
        key,
        url: response.data.signedUrl,
        uploadedAt: Date.now(),
        expiresAt: calculateExpiryMs(ttlSeconds),
        loading: false,
        uploading: false,
        uploadingBar: 100,
        pendingDelete: false,
        savedToCloud: true,
        error: undefined,
        mediaNotFoundOrCorrupt: false,
        savedToFolder: folder,
        metadata,
      };
    } catch (err) {
      const error = err as Error;
      return rejectWithValue({ message: error.message || 'Failed to upload media' });
    }
  },
);

export const deleteMedia = createAppAsyncThunk<string, DeleteMediaArgs>(
  'media/deleteMedia',
  async ({ propertyID, folder, key }, { extra: { config }, rejectWithValue }) => {
    const { apiService } = config;

    try {
      const params = new URLSearchParams({
        key,
        folder,
        propertyID,
        requestType: FileRequestType.DeleteImage,
      });
      const endpoint = `${getApiEndpoints().core.storage.deleteMedia}?${params.toString()}`;
      const response = await apiService.delete<{ message: string; data?: { mediaDeleted?: Record<string, string> } }>(
        endpoint,
      );

      if (response.message !== 'DeleteSuccess') {
        throw new Error('Delete failed');
      }

      return key;
    } catch (err) {
      const error = err as Error;
      return rejectWithValue({ message: error.message || 'Failed to delete media' });
    }
  },
);

export const deleteMediaBatch = createAppAsyncThunk<BatchDeleteMediaResult, DeleteMediaBatchArgs>(
  'media/deleteMediaBatch',
  async ({ propertyID, folder, keys }, { extra: { config }, rejectWithValue }) => {
    const { apiService } = config;

    try {
      const normalizedKeys = normalizeKeys(keys);
      const endpoint = getApiEndpoints().core.storage.deleteMediaBatch;
      const response = await apiService.post<Record<string, string>>(endpoint, {
        requestType: FileRequestType.DeleteImages,
        propertyID,
        folder,
        filesKeys: normalizedKeys,
      });

      if (!isRecordString(response)) {
        throw new Error('Invalid batch delete response');
      }

      const deletedKeys: string[] = [];
      const failed: BatchDeleteMediaResult['failed'] = [];

      normalizedKeys.forEach((key) => {
        const result = response[key];
        if (result === 'DeleteSuccess') {
          deletedKeys.push(key);
          return;
        }

        failed.push({
          key,
          error: result || 'Delete failed',
        });
      });

      return { deletedKeys, failed };
    } catch (err) {
      const error = err as Error;
      return rejectWithValue({ message: error.message || 'Failed to delete media batch' });
    }
  },
);
